import React, { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * AuthedImage — fetches an image via axios (Authorization header attached
 * by the api interceptor) and renders it via a blob URL. Used for
 * tenant-scoped photo endpoints where the browser `<img src>` cannot send
 * a Bearer token by itself.
 *
 * Props:
 *  - src     backend-relative URL (e.g. "/api/photos/serve-authenticated/xxx/thumb")
 *  - alt     alt text
 *  - className, style, onError, testid
 */
const cache = new Map(); // src -> objectURL (memoised per session)

export default function AuthedImage({ src, alt = "", className = "", style, onError, testid }) {
  const [url, setUrl] = useState(() => (src && cache.get(src)) || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!src) { setUrl(null); return; }
    if (cache.has(src)) { setUrl(cache.get(src)); return; }
    let alive = true;
    const path = src.startsWith("/api/") ? src.slice(4) : src;
    api.get(path, { responseType: "blob" })
      .then((res) => {
        if (!alive) return;
        const obj = URL.createObjectURL(res.data);
        cache.set(src, obj);
        setUrl(obj);
      })
      .catch(() => {
        if (!alive) return;
        setFailed(true);
        onError?.();
      });
    return () => { alive = false; };
  }, [src, onError]);

  if (failed || !url) return null;
  return <img src={url} alt={alt} className={className} style={style} data-testid={testid} loading="lazy" />;
}
